import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Search, Sparkles } from 'lucide-react';
import api from '../../api/client';

/**
 * Shows what the AI would actually pull up for a given question. The only honest
 * way to tell whether the store is helping - counts say nothing about relevance.
 */
export default function RetrievalPreview({ shops }) {
  const { t } = useTranslation();
  const [shopId, setShopId] = useState(shops[0]?.id || '');
  const [question, setQuestion] = useState('');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!shopId || question.trim().length < 3) return;

    setLoading(true);
    setError('');
    try {
      const { data } = await api.post('/knowledge/preview', { shop_id: shopId, question });
      setResults(data.data);
    } catch (err) {
      setError(err.response?.data?.error || t('knowledge.preview_failed'));
      setResults(null);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5 mb-6">
      <div className="flex items-center gap-2 mb-1">
        <Sparkles className="h-4 w-4 text-primary-600" />
        <h2 className="font-semibold text-gray-900">{t('knowledge.preview_title')}</h2>
      </div>
      <p className="text-sm text-gray-500 mb-4">{t('knowledge.preview_hint')}</p>

      <form onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-2">
        <select
          value={shopId}
          onChange={(e) => setShopId(e.target.value)}
          className="rounded-lg border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 text-sm"
        >
          {shops.map((s) => (
            <option key={s.id} value={s.id}>{s.name}</option>
          ))}
        </select>
        <input
          type="text"
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          placeholder={t('knowledge.preview_placeholder')}
          className="flex-1 rounded-lg border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 text-sm"
        />
        <button
          type="submit"
          disabled={loading}
          className="flex items-center justify-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-700 disabled:opacity-50 transition-colors"
        >
          <Search className="h-4 w-4" />
          {loading ? t('common.loading') : t('knowledge.preview_search')}
        </button>
      </form>

      {error && <p className="mt-3 text-sm text-red-600">{error}</p>}

      {results && results.length === 0 && (
        <p className="mt-4 text-sm text-gray-500">{t('knowledge.preview_no_results')}</p>
      )}

      {results && results.length > 0 && (
        <ul className="mt-4 space-y-3">
          {results.map((chunk) => (
            <li key={chunk.id} className="border border-gray-200 rounded-lg p-3">
              <div className="flex items-center justify-between gap-3 mb-1">
                <span className="text-xs font-medium text-gray-500">
                  {t(`knowledge.source_${chunk.source_type}`)}
                </span>
                <span className="text-xs font-semibold text-primary-700">
                  {Math.round(chunk.similarity * 100)}%
                </span>
              </div>
              <p className="text-sm font-medium text-gray-900 line-clamp-2">{chunk.question}</p>
              {chunk.answer && <p className="text-sm text-gray-600 line-clamp-2">{chunk.answer}</p>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
